import React from 'react';

import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import BarChartIcon from '@mui/icons-material/BarChart';
import BusinessIcon from '@mui/icons-material/Business';
import CollectionsOutlinedIcon from '@mui/icons-material/CollectionsOutlined';
import HotelIcon from '@mui/icons-material/Hotel';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SummarizeIcon from '@mui/icons-material/Summarize';
import TravelExploreOutlinedIcon from '@mui/icons-material/TravelExploreOutlined';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { Button, Paper, Stack, Typography } from '@mui/material';

import FieldGrid from '../../shared/FieldGrid';

import type { DetailField } from './types';

const customLinkIcons = {
  'Current Deal Performance': <BarChartIcon fontSize="small" />,
  'Experiences in Destination': <TravelExploreOutlinedIcon fontSize="small" />,
  'FX Price Adjustments': <AttachMoneyIcon fontSize="small" />,
  'Hidden Fields': <VisibilityOffIcon fontSize="small" />,
  'NPS Results': <SummarizeIcon fontSize="small" />,
  'Performance in Similar Deals': <BarChartIcon fontSize="small" />,
  'Room Assets': <ImageOutlinedIcon fontSize="small" />,
  'View In Scoopon': <OpenInNewIcon fontSize="small" />,
  'View Offer in Admin': <CollectionsOutlinedIcon fontSize="small" />,
  'View Vendor in Admin': <BusinessIcon fontSize="small" />,
  'View in LE': <HotelIcon fontSize="small" />,
} satisfies Partial<Record<string, React.ReactNode>>;

const customLinkGroups = [
  { labels: ['View in LE', 'View In Scoopon', 'View Offer in Admin', 'View Vendor in Admin'], title: 'Quick Access' },
  {
    labels: [
      'FX Price Adjustments',
      'Hidden Fields',
      'Experiences in Destination',
      'NPS Results',
      'Room Assets',
      'Current Deal Performance',
      'Performance in Similar Deals',
    ],
    title: 'Salesforce Custom Links',
  },
];

function getCustomLinkHref(value: string) {
  if (value.startsWith('http') || value.startsWith('/')) {
    return value;
  }

  return undefined;
}

interface Props {
  fields: Array<DetailField>;
}

export default function CustomLinksPanel({ fields }: Props) {
  return (
    <FieldGrid columns={2}>
      {customLinkGroups.map((group) => {
        const groupFields = group.labels.flatMap((label) => {
          const field = fields.find((field) => field.label === label);

          return field ? [field] : [];
        });

        if (groupFields.length === 0) {
          return null;
        }

        return (
          <Paper key={group.title} variant="outlined" sx={{ p: 2 }}>
            <Typography color="text.secondary" fontWeight={600} sx={{ mb: 1.5 }} variant="subtitle2">
              {group.title}
            </Typography>
            <Stack spacing={1}>
              {groupFields.map((field) => {
                const href = getCustomLinkHref(field.value);

                return (
                  <Button
                    key={field.label}
                    disabled={!href}
                    fullWidth
                    href={href}
                    size="small"
                    startIcon={customLinkIcons[field.label]}
                    sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
                    target={href?.startsWith('http') ? '_blank' : undefined}
                    rel={href?.startsWith('http') ? 'noopener noreferrer' : undefined}
                    variant="outlined"
                  >
                    {field.label}
                  </Button>
                );
              })}
            </Stack>
          </Paper>
        );
      })}
    </FieldGrid>
  );
}
